import React, { useRef, useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import locationAPI from '../api/locationAPI';
import AdminNavbar from './AdminNavbar';

const LocationPicker = () => {
  const [location, setLocation] = useState({ lat: 13.0827, lng: 80.2707 });
  const [locationName, setLocationName] = useState('');
  const [loading, setLoading] = useState(true);
  const markerRef = useRef(null);

  // Fetch saved company location on mount
  const fetchCompanyLocation = useCallback(async () => {
    try {
      const res = await locationAPI.get('/location');
      const loc = res.data.location;
      if (loc?.lat && loc?.lng) {
        setLocation({ lat: loc.lat, lng: loc.lng });
      }
    } catch (err) {
      console.error('Failed to fetch location:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanyLocation();
  }, [fetchCompanyLocation]);

  // Recenter map component
  const RecenterMap = ({ lat, lng }) => {
    const map = useMap();
    useEffect(() => {
      map.setView([lat, lng], map.getZoom(), { animate: true });
    }, [lat, lng, map]);
    return null;
  };

  // Handle map click to update location
  const handleMapClick = (e) => {
    setLocation({ lat: e.latlng.lat, lng: e.latlng.lng });
  };

  // Use browser's current location
 const useCurrentLocation = () => {
  if (!navigator.geolocation) {
    alert('Geolocation not supported, using default location');
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
    async (error) => {
      console.warn('GPS failed, using IP fallback', error.message);
      try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        setLocation({ lat: data.latitude, lng: data.longitude });
      } catch {
        alert('Unable to get location');
      }
    },
    { enableHighAccuracy: true }
  );
};


  // Search location by name using Nominatim API
  const searchLocation = () => {
    if (!locationName.trim()) return alert('Enter a location name');

    const query = encodeURIComponent(locationName.trim());
    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}`)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.length > 0) {
          const result = data[0]; // First result
          setLocation({
            lat: parseFloat(result.lat),
            lng: parseFloat(result.lon),
          });
        } else {
          alert('Location not found');
        }
      })
      .catch((error) => {
        console.error('Search error:', error);
        alert('Failed to search location');
      });
  };

  // Save location to backend
  const saveLocationToBackend = async () => {
    alert(`Latitude: ${location.lat.toFixed(6)}, Longitude: ${location.lng.toFixed(6)}`);
    try {
      const res = await locationAPI.post('/location', {
        lat: location.lat,
        lng: location.lng,
      });
      alert(res.data.message || 'Location added successfully');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save location');
    }
  };

  // Custom map marker component
  const LocationMarker = () => {
    useMapEvents({ click: handleMapClick });

    return (
      <Marker
        position={[location.lat, location.lng]}
        icon={L.icon({
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
        })}
        ref={markerRef}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-white p-2">
      <AdminNavbar />

      <div className="max-w-4xl mx-auto mt-20 bg-white rounded-2xl shadow-lg p-4 space-y-2">
        <h2 className="text-2xl font-semibold text-center text-purple-600">Select Office Location</h2>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            placeholder="Search location name"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            className="border border-gray-300 rounded-lg p-2 flex-1 focus:outline-none focus:ring-2 focus:ring-purple-300"
          />
          <button
            onClick={searchLocation}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
          >
            Search
          </button>
          <button
            onClick={useCurrentLocation}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Use GPS
          </button>
        </div>

        {!loading && (
          <div className="relative z-0"  >
          <MapContainer
            center={[location.lat, location.lng]}
            zoom={15}
            scrollWheelZoom={true}
            style={{ height: '350px', width: '100%' }}
            className="rounded-lg shadow-md"
          >
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <LocationMarker />
            <RecenterMap lat={location.lat} lng={location.lng} />
          </MapContainer>
          </div>
        )}

        <div className="text-center text-gray-700">
          <p><strong>Latitude:</strong> {location.lat.toFixed(6)}</p>
          <p><strong>Longitude:</strong> {location.lng.toFixed(6)}</p>
        </div>

        <div className="text-center">
          <button
            onClick={saveLocationToBackend}
            className="bg-purple-700 hover:bg-purple-800 text-white px-20 py-2 rounded-xl transition-all shadow-md"
          >
            Add Location
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationPicker;
